import React, { useState, useEffect } from 'react';
import api from '../api';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { format } from 'date-fns';

const TYPE_OPTS = ['bug', 'ui', 'feature', 'performance', 'other'];
const PRIORITY_OPTS = ['low', 'medium', 'high', 'critical'];
const STATUS_OPTS = ['open', 'in_progress', 'resolved', 'closed', 'rejected'];

const PRIORITY_COLORS = { low: '#2f9e44', medium: '#e67700', high: '#c92a2a', critical: '#862e9c' };
const STATUS_CLASSES = { open: 'badge-pending', in_progress: 'badge-in_progress', resolved: 'badge-completed', closed: 'badge-offline', rejected: 'badge-not_interested' };

export default function Issues() {
  const { user } = useAuth();
  const [issues, setIssues] = useState([]);
  const [stats, setStats] = useState({});
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [comment, setComment] = useState('');
  const [form, setForm] = useState({
    title: '', description: '', type: 'bug', priority: 'medium',
    stepsToReproduce: '', expectedBehavior: '', actualBehavior: '', environment: ''
  });

  const fetchIssues = () => {
    api.get('/issues').then(r => setIssues(r.data)).catch(() => {});
    api.get('/issues/stats/summary').then(r => setStats(r.data)).catch(() => {});
  };

  useEffect(() => {
    fetchIssues();
    if (user?.role === 'superadmin') api.get('/users').then(r => setUsers(r.data)).catch(() => {});
  }, [user]);

  const createIssue = async (e) => {
    e.preventDefault();
    try {
      await api.post('/issues', form);
      toast.success('Issue reported');
      setShowCreate(false);
      setForm({ title: '', description: '', type: 'bug', priority: 'medium', stepsToReproduce: '', expectedBehavior: '', actualBehavior: '', environment: '' });
      fetchIssues();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const updateIssue = async (id, data) => {
    try {
      const { data: updated } = await api.put('/issues/' + id, data);
      toast.success('Updated');
      setIssues(prev => prev.map(i => i._id === id ? updated : i));
      if (showDetail?._id === id) setShowDetail(updated);
    } catch (e) { toast.error('Failed'); }
  };

  const addComment = async () => {
    if (!comment.trim()) return;
    try {
      const { data: updated } = await api.post('/issues/' + showDetail._id + '/comment', { text: comment });
      setShowDetail(updated);
      setComment('');
    } catch (e) { toast.error('Failed to add comment'); }
  };

  const deleteIssue = async (id) => {
    if (!window.confirm('Delete this issue?')) return;
    try {
      await api.delete('/issues/' + id);
      toast.success('Deleted');
      setShowDetail(null);
      fetchIssues();
    } catch (e) { toast.error('Failed'); }
  };

  const openDetail = async (issue) => {
    try {
      const { data } = await api.get('/issues/' + issue._id);
      setShowDetail(data);
    } catch (e) { setShowDetail(issue); }
  };

  const filtered = filter === 'all' ? issues : issues.filter(i => i.status === filter);

  return (
    <div className="fadein">
      <div className="page-header">
        <div>
          <div className="page-title">Issues</div>
          <div className="page-subtitle">Report and track bugs, issues, and feature requests</div>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => setShowCreate(true)}>+ Report Issue</button>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10, marginBottom: 20 }}>
        {[
          { label: 'All', value: stats.all || 0, key: 'all' },
          { label: 'Open', value: stats.open || 0, key: 'open' },
          { label: 'In Progress', value: stats.inProgress || 0, key: 'in_progress' },
          { label: 'Resolved', value: stats.resolved || 0, key: 'resolved' },
          { label: 'Closed', value: stats.closed || 0, key: 'closed' },
        ].map(s => (
          <div key={s.key} onClick={() => setFilter(s.key)} className="stat-card"
            style={{ cursor: 'pointer', border: filter === s.key ? '1.5px solid var(--accent)' : undefined }}>
            <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-1)' }}>{s.value}</div>
            <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 3 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Issues list */}
      <div className="card">
        {filtered.length === 0 ? (
          <div className="empty"><p>No issues found</p></div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Issue</th>
                <th>Type</th>
                <th>Priority</th>
                <th>Status</th>
                <th>Reporter</th>
                {user?.role === 'superadmin' && <th>Assigned To</th>}
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(issue => (
                <tr key={issue._id} onClick={() => openDetail(issue)} style={{ cursor: 'pointer' }}>
                  <td>
                    <div style={{ fontWeight: 500 }}>{issue.title}</div>
                    {issue.description && <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 1, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{issue.description}</div>}
                  </td>
                  <td><span style={{ fontSize: 12, color: 'var(--text-2)', textTransform: 'capitalize' }}>{issue.type}</span></td>
                  <td><span style={{ fontSize: 12, fontWeight: 600, color: PRIORITY_COLORS[issue.priority] || 'var(--text-2)' }}>{issue.priority}</span></td>
                  <td><span className={`badge ${STATUS_CLASSES[issue.status] || 'badge-pending'}`}>{issue.status.replace('_', ' ')}</span></td>
                  <td style={{ fontSize: 13 }}>{issue.reportedBy?.name}</td>
                  {user?.role === 'superadmin' && <td style={{ fontSize: 13 }}>{issue.assignedTo?.name || '—'}</td>}
                  <td style={{ fontSize: 12.5, color: 'var(--text-3)' }}>{issue.createdAt ? format(new Date(issue.createdAt), 'MMM d, yyyy') : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create modal */}
      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
            <div className="modal-head">
              <h2>Report Issue</h2>
              <button className="modal-close" onClick={() => setShowCreate(false)}>x</button>
            </div>
            <div className="modal-body">
              <form onSubmit={createIssue}>
                <div className="form-group">
                  <label>Title</label>
                  <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required placeholder="Short description of the issue" />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea rows={3} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Detailed explanation..." style={{ resize: 'vertical' }} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div className="form-group">
                    <label>Type</label>
                    <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
                      {TYPE_OPTS.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Priority</label>
                    <select value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}>
                      {PRIORITY_OPTS.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label>Steps to Reproduce</label>
                  <textarea rows={2} value={form.stepsToReproduce} onChange={e => setForm(f => ({ ...f, stepsToReproduce: e.target.value }))} placeholder="1. Go to...&#10;2. Click on...&#10;3. See error" style={{ resize: 'vertical' }} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div className="form-group">
                    <label>Expected Behavior</label>
                    <textarea rows={2} value={form.expectedBehavior} onChange={e => setForm(f => ({ ...f, expectedBehavior: e.target.value }))} placeholder="What should happen" style={{ resize: 'vertical' }} />
                  </div>
                  <div className="form-group">
                    <label>Actual Behavior</label>
                    <textarea rows={2} value={form.actualBehavior} onChange={e => setForm(f => ({ ...f, actualBehavior: e.target.value }))} placeholder="What actually happened" style={{ resize: 'vertical' }} />
                  </div>
                </div>
                <div className="form-group">
                  <label>Environment</label>
                  <input value={form.environment} onChange={e => setForm(f => ({ ...f, environment: e.target.value }))} placeholder="e.g. Chrome 120 / Windows 11 / Mobile" />
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-primary" type="submit" style={{ flex: 1 }}>Submit Issue</button>
                  <button className="btn btn-secondary" type="button" onClick={() => setShowCreate(false)}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Detail modal */}
      {showDetail && (
        <div className="modal-overlay" onClick={() => setShowDetail(null)}>
          <div className="modal" style={{ maxWidth: 600 }} onClick={e => e.stopPropagation()}>
            <div className="modal-head">
              <h2 style={{ fontSize: 14 }}>{showDetail.title}</h2>
              <button className="modal-close" onClick={() => setShowDetail(null)}>x</button>
            </div>
            <div className="modal-body">
              {/* Meta row */}
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
                <span className={`badge ${STATUS_CLASSES[showDetail.status] || 'badge-pending'}`}>{showDetail.status.replace('_', ' ')}</span>
                <span style={{ fontSize: 12, fontWeight: 600, color: PRIORITY_COLORS[showDetail.priority] }}>{showDetail.priority}</span>
                <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{showDetail.type}</span>
                <span style={{ fontSize: 12, color: 'var(--text-3)' }}>by {showDetail.reportedBy?.name}</span>
                <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{showDetail.createdAt ? format(new Date(showDetail.createdAt), 'MMM d, yyyy h:mm a') : ''}</span>
              </div>

              {/* Details */}
              {showDetail.description && (
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-3)', marginBottom: 4 }}>DESCRIPTION</div>
                  <div style={{ fontSize: 13, color: 'var(--text-1)', lineHeight: 1.6 }}>{showDetail.description}</div>
                </div>
              )}
              {showDetail.stepsToReproduce && (
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-3)', marginBottom: 4 }}>STEPS TO REPRODUCE</div>
                  <div style={{ fontSize: 13, color: 'var(--text-1)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{showDetail.stepsToReproduce}</div>
                </div>
              )}
              {(showDetail.expectedBehavior || showDetail.actualBehavior) && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                  {showDetail.expectedBehavior && (
                    <div style={{ background: '#ebfbee', borderRadius: 8, padding: '10px 12px', border: '1px solid #b2f2bb' }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: '#2f9e44', marginBottom: 4 }}>EXPECTED</div>
                      <div style={{ fontSize: 12.5, color: 'var(--text-1)' }}>{showDetail.expectedBehavior}</div>
                    </div>
                  )}
                  {showDetail.actualBehavior && (
                    <div style={{ background: '#fff5f5', borderRadius: 8, padding: '10px 12px', border: '1px solid #ffc9c9' }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: '#c92a2a', marginBottom: 4 }}>ACTUAL</div>
                      <div style={{ fontSize: 12.5, color: 'var(--text-1)' }}>{showDetail.actualBehavior}</div>
                    </div>
                  )}
                </div>
              )}
              {showDetail.environment && (
                <div style={{ marginBottom: 14, fontSize: 12.5, color: 'var(--text-3)' }}>
                  Environment: {showDetail.environment}
                </div>
              )}

              {/* Admin controls */}
              {user?.role === 'superadmin' && (
                <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', padding: '12px', background: 'var(--surface-2)', borderRadius: 8, border: '1px solid var(--border-light)' }}>
                  <select value={showDetail.status} onChange={e => updateIssue(showDetail._id, { status: e.target.value })} style={{ width: 'auto', minWidth: 130 }}>
                    {STATUS_OPTS.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                  </select>
                  <select value={showDetail.assignedTo?._id || ''} onChange={e => updateIssue(showDetail._id, { assignedTo: e.target.value })} style={{ width: 'auto', minWidth: 160 }}>
                    <option value="">Unassigned</option>
                    {users.map(u => <option key={u._id} value={u._id}>{u.name}</option>)}
                  </select>
                  <select value={showDetail.priority} onChange={e => updateIssue(showDetail._id, { priority: e.target.value })} style={{ width: 'auto', minWidth: 100 }}>
                    {PRIORITY_OPTS.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                  <button className="btn btn-xs btn-danger" onClick={() => deleteIssue(showDetail._id)}>Delete</button>
                </div>
              )}

              {/* Comments */}
              <div style={{ borderTop: '1px solid var(--border-light)', paddingTop: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-3)', marginBottom: 10 }}>COMMENTS ({showDetail.comments?.length || 0})</div>
                {showDetail.comments?.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
                    {showDetail.comments.map(c => (
                      <div key={c._id} style={{ background: 'var(--surface-2)', borderRadius: 6, padding: '9px 12px', border: '1px solid var(--border-light)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-1)' }}>{c.user?.name || 'Unknown'}</span>
                          <span style={{ fontSize: 11, color: 'var(--text-3)' }}>{c.createdAt ? format(new Date(c.createdAt), 'MMM d, h:mm a') : ''}</span>
                        </div>
                        <div style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.5 }}>{c.text}</div>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8 }}>
                  <input value={comment} onChange={e => setComment(e.target.value)} placeholder="Add a comment..." style={{ flex: 1 }} onKeyDown={e => { if (e.key === 'Enter') addComment(); }} />
                  <button className="btn btn-primary btn-sm" onClick={addComment}>Post</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
